
import React, { useState, useEffect, useCallback } from "react";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import axios from "axios";
import "../../../assets/css/TopManagementSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function hasImage(item) {
  return (
    typeof item?.passport === "string" &&
    item.passport.trim() !== "" &&
    item.passport !== "undefined" &&
    item.passport !== "null"
  );
}

function TopManagementSection({
  datacontent = [],
  initialPagination,
  id,
  section,
}) {
  const [newsList, setNewsList] = useState(datacontent || []);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(
    initialPagination || {
      current_page: 1,
      last_page: 1,
    }
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const response = await axios.get(
        `${BASE_URL}posdata?pageName[eq]=${id}&columes[eq]=title_show_echo_01&ministry[eq]=1&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&display01[eq]=2&page=${page}&per_page=10`
      );

      const allData = response?.data?.allData || [];

      setNewsList(allData);
      setActiveIndex(allData.length > 0 ? 0 : null);

      setPagination(
        response?.data?.pagination || {
          current_page: page,
          last_page: 1,
        }
      );
    } catch (err) {
      console.error("Unable to load top management content:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  useEffect(() => {
    if (datacontent?.length && page === 1) {
      setNewsList(datacontent);
      setActiveIndex(0);
    }
  }, [datacontent, page]);

  const toggleTopManagement = (index) => {
    setActiveIndex((current) => (current === index ? null : index));
  };

  const currentPage = Number(pagination?.current_page || page);
  const lastPage = Math.max(1, Number(pagination?.last_page || 1));

  const goToPage = (nextPage) => {
    if (
      nextPage < 1 ||
      nextPage > lastPage ||
      nextPage === page ||
      loading
    ) {
      return;
    }

    setPage(nextPage);
  };

  if (!loading && error && newsList.length === 0) {
    return (
      <section id="TopManagement" className="tm-modern section">
        <div className="container">
          <div className="tm-modern-message" role="alert">
            <p>Management information could not be loaded.</p>
            <button type="button" onClick={fetchNews}>
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="TopManagement" className="tm-modern section">
      <div className="container">
        {/* Section heading */}
        <div className="tm-modern-heading">
          <div className="tm-modern-heading-copy">
            <span className="tm-modern-eyebrow">
              LEADERSHIP &amp; GOVERNANCE
            </span>

            <h2>{section}</h2>

            {/* <p>
              Meet the leadership and explore the responsibilities,
              profiles and key information of the Ministry's management.
            </p> */}
          </div>

          <div className="tm-modern-heading-mark" aria-hidden="true">
            <i className="fa-solid fa-landmark" />
          </div>
        </div>

        {loading && (
          <div className="tm-modern-status" role="status">
            Loading management information...
          </div>
        )}

        {error && newsList.length > 0 && (
          <div className="tm-modern-inline-error" role="alert">
            We could not refresh the information. The previously loaded
            entries are still available.
          </div>
        )}

        {/* Management accordion */}
        {!loading && newsList.length === 0 ? (
          <div className="tm-modern-message">
            No management information is available at the moment.
          </div>
        ) : (
          <div className="tm-modern-list">
            {newsList.map((item, index) => {
              const isActive = activeIndex === index;
              const imageAvailable = hasImage(item);
              const imageUrl = imageAvailable
                ? `${BACKEND_URL}upload/${item.passport}`
                : null;

              const panelId = `tm-modern-panel-${item.id || index}`;
              const triggerId = `tm-modern-trigger-${item.id || index}`;

              return (
                <article
                  className={`tm-modern-item ${
                    isActive ? "tm-modern-item-active" : ""
                  }`}
                  key={item.id || index}
                >
                  <button
                    type="button"
                    id={triggerId}
                    className="tm-modern-trigger"
                    aria-expanded={isActive}
                    aria-controls={panelId}
                    onClick={() => toggleTopManagement(index)}
                  >
                    <span className="tm-modern-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span className="tm-modern-trigger-title">
                      {item.title || "Management Information"}
                    </span>

                    <span className="tm-modern-toggle-icon">
                      <i
                        className={`fa-solid ${
                          isActive ? "fa-minus" : "fa-plus"
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                  </button>

                  <div
                    id={panelId}
                    className={`tm-modern-panel ${
                      isActive ? "tm-modern-panel-open" : ""
                    }`}
                    role="region"
                    aria-labelledby={triggerId}
                    aria-hidden={!isActive}
                    inert={!isActive}
                  >
                    <div className="tm-modern-panel-inner">
                      {imageAvailable && (
                        <div className="tm-modern-image-wrap">
                          <img
                            src={imageUrl}
                            alt={item.title || "Management"}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      )}

                      <div className="tm-modern-content">
                        {item.title && (
                          <span className="tm-modern-content-label">
                            MANAGEMENT PROFILE
                          </span>
                        )}

                        <div className="tm-modern-body">
                          <HTMLDisplay
                            html={item.story01 || ""}
                            type="title"
                          />

                          <HTMLDisplay
                            html={item.story01 || ""}
                            type="body"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {lastPage > 1 && (
          <nav
            className="tm-modern-pagination"
            aria-label="Management information pages"
          >
            <span className="tm-modern-page-info">
              Page <strong>{currentPage}</strong> of{" "}
              <strong>{lastPage}</strong>
            </span>

            <div className="tm-modern-page-controls">
              <button
                type="button"
                onClick={() => goToPage(1)}
                disabled={currentPage <= 1 || loading}
                aria-label="First page"
              >
                <i className="fa-solid fa-angles-left" />
              </button>

              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
                aria-label="Previous page"
              >
                <i className="fa-solid fa-angle-left" />
              </button>

              <span className="tm-modern-current-page">
                {currentPage}
              </span>

              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= lastPage || loading}
                aria-label="Next page"
              >
                <i className="fa-solid fa-angle-right" />
              </button>

              <button
                type="button"
                onClick={() => goToPage(lastPage)}
                disabled={currentPage >= lastPage || loading}
                aria-label="Last page"
              >
                <i className="fa-solid fa-angles-right" />
              </button>
            </div>
          </nav>
        )}
      </div>
    </section>
  );
}

export default React.memo(TopManagementSection);
